/**
 * AdoraCare -- the ONE clinic-local age derivation (frontend bundle mirror of
 * lib/domain/patient/age.ts; PATIENT-INTAKE-PREFILL-AND-EXPERIENCE-V1, F-5).
 *
 * Identical algorithm to the backend util (its own bundle -- the established
 * backend/frontend split, cf. istDateOf vs datetime-ist). DOB is primary: a
 * known DOB derives a read-only age from the CLINIC-LOCAL current date
 * (clinics.timezone, IANA, DST-safe via Intl); an approximate age is allowed
 * ONLY when the DOB is explicitly unknown, and a DOB is NEVER fabricated from
 * an age. The intake surfaces (drawer + patient mode) call THIS so age and DOB
 * can never disagree; broad Patient 360 convergence is Package B.
 */

export const DEFAULT_CLINIC_TIMEZONE = 'Asia/Kolkata';

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export interface Ymd {
  readonly year: number;
  readonly month: number;
  readonly day: number;
}

/** ASCII month abbreviations for the clinic-local date formatter. */
const CLINIC_MONTHS: readonly string[] = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/**
 * THE ONE clinic-local DATE formatter (PATIENT-360-AND-JOURNEY-TRUTH-
 * CORRECTIVE-V1, F-3 timezone corrective). Formats an arbitrary ISO instant
 * as a calendar date IN THE CLINIC'S IANA TIMEZONE, e.g. "8 May 2026". This
 * is TRUE clinic-local, DST-safe multi-tenant behavior: it reuses
 * clinicLocalDate (Intl + the clinic's IANA `clinics.timezone`), degrading to
 * the tenant default zone for a blank/invalid zone -- never a silent UTC read
 * and never the old fixed-IST off-by-one. Patient Quick View and Patient 360
 * render the registered date through THIS with the SAME clinic timezone, so
 * they can never disagree and a US clinic shows its OWN local date. Returns ''
 * for a null/unparseable value so the caller can fall back.
 */
export function formatClinicLocalDate(
  iso: string | null | undefined,
  timeZone?: string | null,
): string {
  if (iso === null || iso === undefined || iso.trim() === '') return '';
  const ms = Date.parse(iso.trim());
  if (Number.isNaN(ms)) return '';
  const { year, month, day } = clinicLocalDate(ms, timeZone);
  if (year === 0 || month < 1 || month > 12) return '';
  return `${day} ${CLINIC_MONTHS[month - 1]} ${year}`;
}

/** Clinic-local calendar date at an instant, for an IANA timezone (DST-safe).
 *  Falls back to the tenant default zone, never a silent UTC read. */
export function clinicLocalDate(nowMs: number, timeZone?: string | null): Ymd {
  const zone = timeZone && timeZone.trim() !== '' ? timeZone.trim() : DEFAULT_CLINIC_TIMEZONE;
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: zone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date(nowMs));
  } catch {
    parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: DEFAULT_CLINIC_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date(nowMs));
  }
  let year = 0;
  let month = 0;
  let day = 0;
  for (const p of parts) {
    if (p.type === 'year') year = Number(p.value);
    else if (p.type === 'month') month = Number(p.value);
    else if (p.type === 'day') day = Number(p.value);
  }
  return { year, month, day };
}

/**
 * THE clinic-local DATE+TIME formatter (the formatClinicLocalDate precedent,
 * extended to a 12-hour clock). Formats an arbitrary ISO instant as a compact
 * date + 12-hour time IN THE CLINIC'S IANA TIMEZONE, e.g. "21 Jul 2026, 12:05
 * PM". TRUE clinic-local, DST-safe multi-tenant behavior via Intl + the
 * clinic's IANA `clinics.timezone`; degrades to the tenant default zone for a
 * blank/invalid zone -- never a silent UTC read and never the fixed-IST
 * off-by-zone. Returns '' for a null/unparseable value so the caller can fall
 * back. The date+time sibling of formatClinicLocalDate and the multi-tenant
 * replacement for the IST-fixed formatIstDateTime on clinic-local surfaces
 * (CALL-CENTER-SURFACE-AND-TRUTH-CORRECTIVE-V1, F-3). The caller renders the
 * IANA zone alongside so the reader always knows the zone.
 */
export function formatClinicLocalDateTime(
  iso: string | null | undefined,
  timeZone?: string | null,
): string {
  if (iso === null || iso === undefined || iso.trim() === '') return '';
  const ms = Date.parse(iso.trim());
  if (Number.isNaN(ms)) return '';
  const zone =
    timeZone && timeZone.trim() !== '' ? timeZone.trim() : DEFAULT_CLINIC_TIMEZONE;
  const opts: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat('en-US', { ...opts, timeZone: zone }).formatToParts(
      new Date(ms),
    );
  } catch {
    parts = new Intl.DateTimeFormat('en-US', {
      ...opts,
      timeZone: DEFAULT_CLINIC_TIMEZONE,
    }).formatToParts(new Date(ms));
  }
  const get = (t: string): string => parts.find((p) => p.type === t)?.value ?? '';
  const year = Number(get('year'));
  const month = Number(get('month'));
  const day = Number(get('day'));
  if (year === 0 || month < 1 || month > 12) return '';
  const hour = get('hour');
  const minute = get('minute');
  const dayPeriod = get('dayPeriod').toUpperCase();
  return `${day} ${CLINIC_MONTHS[month - 1]} ${year}, ${hour}:${minute} ${dayPeriod}`;
}

function parseDobYmd(dobIso: string): Ymd | null {
  const trimmed = dobIso.trim();
  if (!ISO_DATE_RE.test(trimmed)) return null;
  const parts = trimmed.split('-');
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return null;
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  const asUtc = new Date(Date.UTC(y, m - 1, d));
  if (
    asUtc.getUTCFullYear() !== y ||
    asUtc.getUTCMonth() !== m - 1 ||
    asUtc.getUTCDate() !== d
  ) {
    return null;
  }
  return { year: y, month: m, day: d };
}

export function ageYearsBetween(dob: Ymd, today: Ymd): number | null {
  let age = today.year - dob.year;
  if (today.month < dob.month || (today.month === dob.month && today.day < dob.day)) {
    age -= 1;
  }
  if (age < 0 || age > 150) return null;
  return age;
}

/** Age in whole years from an ISO DOB using the clinic-local current date. */
export function deriveAgeFromDob(
  dobIso: string,
  nowMs: number,
  timeZone?: string | null,
): number | null {
  const dob = parseDobYmd(dobIso);
  if (dob === null) return null;
  return ageYearsBetween(dob, clinicLocalDate(nowMs, timeZone));
}

export interface AgeView {
  readonly ageYears: number | null;
  readonly derivedFromDob: boolean;
  readonly isApproximate: boolean;
  readonly dobKnown: boolean;
}

/** DOB wins (age display-only); age-only is explicitly approximate; a DOB is
 *  never fabricated from an age. */
export function buildAgeView(
  dobIso: string | null,
  approximateAgeYears: number | null,
  nowMs: number,
  timeZone?: string | null,
): AgeView {
  if (dobIso !== null && dobIso.trim() !== '') {
    const derived = deriveAgeFromDob(dobIso, nowMs, timeZone);
    return { ageYears: derived, derivedFromDob: derived !== null, isApproximate: false, dobKnown: true };
  }
  if (
    approximateAgeYears !== null &&
    Number.isInteger(approximateAgeYears) &&
    approximateAgeYears >= 0 &&
    approximateAgeYears <= 150
  ) {
    return { ageYears: approximateAgeYears, derivedFromDob: false, isApproximate: true, dobKnown: false };
  }
  return { ageYears: null, derivedFromDob: false, isApproximate: false, dobKnown: false };
}
